import React, { useState, useEffect, useRef, forwardRef } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import TEXT_CLASSNAMES from '../Text/responsiveClassnames';
import {
  BASE_INPUT,
  BACKGROUND_COLOR,
  BORDER_COLOR,
  HINT_COLOR,
  INPUT_TEXT_COLOR,
} from './classnames';
import { white, baseMain } from '../styles/colors';
import transition from '../styles/transition';
import {
  TextFieldProps,
  TextFieldBackgroundState,
  TextFieldBorderState,
  TextFieldInputState,
} from './types';
import Adornment from './Adornment';

const StyledInput = styled.input<{ hintColor: string; darkMode?: boolean }>`
  ${({ hintColor, darkMode }) => `
    &::-webkit-input-placeholder {
      color: ${hintColor};
    }
    &:-moz-placeholder { /* Firefox 18- */
      color: ${hintColor};
    }
    &::-moz-placeholder {  /* Firefox 19+ */
      color: ${hintColor};
    }
    &:-ms-input-placeholder {
      color: ${hintColor};
    }
    &::placeholder {
      color: ${hintColor};
    }
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      transition: background-color 5000s ease-in-out 0s;
      -webkit-text-fill-color: ${darkMode ? white : baseMain} !important;
    }
  `}
`;

/**
 * TextField component
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      className = '',
      wrapperClassName = '',
      innerClassName = '',
      chat,
      disabled,
      error,
      errorText,
      full,
      hint,
      noBorders,
      check,
      darkMode,
      inputClassName,
      autoComplete,
      autoFocus,
      defaultValue,
      endAdornment,
      adornmentClassNames,
      id,
      inputProps,
      label,
      description,
      maxLength,
      name,
      onChange: onChangeProp,
      onChangeValidation,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      onKeyDown,
      startAdornment,
      type,
      value,
      'data-testid': dataTestId,
      'data-cy': dataCy,
      textType = 'body3',
      responsive = 'sm',
      wrapperStyles,
      ...textFieldProps
    },
    ref,
  ) => {
    const inputElement = useRef<HTMLInputElement | null>(null);
    const [focused, setFocused] = useState<boolean>(false);

    useEffect(() => {
      if (autoFocus && inputElement.current) {
        inputElement.current.focus();
      }
    }, [autoFocus]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (
        typeof onChangeProp === 'function' &&
        (typeof onChangeValidation !== 'function' ||
          onChangeValidation(e.target.value))
      ) {
        onChangeProp(e);
      }
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      if (typeof onFocusProp === 'function') {
        onFocusProp(e);
      }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (typeof onBlurProp === 'function') {
        onBlurProp(e);
      }
    };

    const assignRef = i => {
      if (typeof ref === 'function') {
        ref(i);
      } else if (ref) {
        ref.current = i;
      }
      inputElement.current = i;
    };

    const fontClassNames = TEXT_CLASSNAMES[textType]
      ? TEXT_CLASSNAMES[textType](responsive === 'md')
      : '';
    let backgroundState: TextFieldBackgroundState = darkMode ? 'dark' : 'light';
    let borderState: TextFieldBorderState = darkMode ? 'dark' : 'light';
    let inputState: TextFieldInputState = darkMode ? 'dark' : 'light';

    if (focused && !chat) {
      borderState = darkMode ? 'dark-focused' : 'dark-focused';
    }
    if (error || errorText) {
      inputState = darkMode ? 'dark-error' : 'light-error';
      borderState = 'error';
    }
    if (disabled) {
      backgroundState = darkMode ? 'dark-disabled' : 'light-disabled';
      inputState = darkMode ? 'dark-disabled' : 'light-disabled';
      borderState = 'disabled';
    }

    let inputPaddingClassNames = '';

    if (!noBorders) {
      inputPaddingClassNames = chat ? 'py-8' : 'py-16';
      if (startAdornment || check || endAdornment) {
        inputPaddingClassNames = cn(
          inputPaddingClassNames,
          startAdornment ? 'pl-8' : 'pl-16',
          endAdornment || check ? 'pr-8' : 'pr-16',
        );
      } else {
        inputPaddingClassNames = cn(inputPaddingClassNames, 'px-16');
      }
    }

    const adornmentProps = {
      startAdornment,
      endAdornment,
      adornmentClassNames,
      chat,
    };

    return (
      <label
        className={cn('inline-flex flex-col', { 'w-full': full }, className)}
        {...textFieldProps}
      >
        {label && (
          <span
            className={cn(
              'font-subtitle3 mb-8',
              darkMode ? 'text-white' : 'text-base-main',
            )}
          >
            {label}
          </span>
        )}
        {description && (
          <p
            className={cn(
              'font-body4 mb-8',
              darkMode ? 'text-base-lighter' : 'text-gray-dark',
            )}
          >
            {description}
          </p>
        )}
        <div
          className={cn(
            'inline-flex',
            {
              [`rounded-5 border border-solid ${BORDER_COLOR[borderState]} ${BACKGROUND_COLOR[backgroundState]}`]: !noBorders,
            },
            wrapperClassName,
          )}
          style={{
            transition: transition(['background-color', 'border']),
            ...wrapperStyles,
          }}
        >
          <div className={cn('flex w-full items-end', innerClassName)}>
            {startAdornment && <Adornment type="start" {...adornmentProps} />}
            <StyledInput
              data-testid={dataTestId}
              data-cy={dataCy}
              className={cn(
                'crehana-ui-input',
                BASE_INPUT,
                INPUT_TEXT_COLOR[inputState],
                fontClassNames,
                inputPaddingClassNames,
                inputClassName,
              )}
              hintColor={HINT_COLOR[backgroundState]}
              darkMode={darkMode}
              placeholder={hint}
              ref={assignRef}
              autoComplete={autoComplete}
              defaultValue={defaultValue ? defaultValue.toString() : undefined}
              disabled={disabled}
              id={id}
              name={name}
              type={type}
              value={value}
              onKeyDown={onKeyDown}
              maxLength={maxLength || undefined}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              {...inputProps}
            />
            {check && <Adornment type="check" {...adornmentProps} />}
            {endAdornment && <Adornment type="end" {...adornmentProps} />}
          </div>
        </div>
        {errorText && (
          <span
            className={cn(
              'font-caption mt-8',
              disabled ? 'text-gray-dark' : 'text-red-main',
            )}
            role="alert"
          >
            {errorText}
          </span>
        )}
      </label>
    );
  },
);

export default TextField;
